import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { eur, Loading } from "@/components/common";
import OffertaCura from "@/components/OffertaCura";
import { useAuth } from "@/context/AuthContext";
import BackNav from "@/components/BackNav";

const DURATIONS = [1, 3, 6, 12];
const DURATION_LABEL = { 1: "1 mese", 3: "3 mesi", 6: "6 mesi", 12: "12 mesi" };

export default function Abbonamenti() {
  const [plans, setPlans] = useState(null);
  const [bonus, setBonus] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    if (user?.role === "customer") {
      api.get("/customer/credits").then((r) => setBonus(r.data.bonus)).catch(() => {});
    }
  }, [user]);

  const grouped = useMemo(() => {
    const g = {};
    (plans || []).forEach((p) => {
      g[p.duration_months] = g[p.duration_months] || [];
      g[p.duration_months].push(p);
    });
    return g;
  }, [plans]);

  if (!plans) return <Loading />;

  const ctaFor = (id) =>
    user ? `/checkout?type=abbonamento&plan=${id}` : "/accedi";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 space-y-12">
      <BackNav />
      <div className="max-w-2xl">
        <p className="eyebrow mb-2">Abbonamenti</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-navy mb-4">Continuità, senza pensieri.</h1>
        <p className="text-body leading-relaxed">
          Scegli durata e frequenza: più lungo è l'abbonamento, più conviene ogni intervento.
          Con 4 interventi al mese il servizio è settimanale (l'annuale settimanale conta 52 interventi).
          {bonus?.status === "disponibile" ? (
            <span data-testid="bonus-banner-inline" className="block mt-2 text-[#4a7c59] font-semibold">
              Hai un bonus prima uscita di {eur(bonus.amount)}: si applica automaticamente agli abbonamenti con almeno 5 interventi (ancora {bonus.days_left} {bonus.days_left === 1 ? "giorno" : "giorni"}).
            </span>
          ) : (
            "Se hai fatto la prima uscita di prova, il bonus si applica agli abbonamenti con almeno 5 interventi."
          )}
        </p>
      </div>

      <OffertaCura testid="highlighted-offer" />

      <section data-testid="subscription-plans-section" className="space-y-10">
        {DURATIONS.map((m) => (
          <div key={m}>
            <h2 className="mb-5">
              <span data-testid={`duration-chip-${m}`} className="inline-block bg-[#1e3a5f] text-white text-lg sm:text-xl font-bold uppercase tracking-widest rounded-full px-6 py-2.5 shadow-sm">
                {DURATION_LABEL[m]}
              </span>
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {(grouped[m] || []).map((p) => {
                const popular = m === 3 && p.frequency === 2;
                return (
                <div key={p.id} className={`card-soft p-6 flex flex-col ${popular ? "!border-[#4a7c59] shadow-md" : ""}`} data-testid={`plan-${m}m-${p.frequency}x`}>
                  {popular && (
                    <span data-testid="plan-badge-piu-scelto" className="self-start text-xs font-bold text-white bg-[#4a7c59] rounded-full px-3 py-1 mb-3">
                      Il più scelto
                    </span>
                  )}
                  <p className="font-semibold text-navy">
                    {p.frequency} intervent{p.frequency === 1 ? "o" : "i"} al mese
                    {m === 12 && p.frequency === 4 && <span className="ml-2 text-xs text-[#4a7c59] font-bold">settimanale</span>}
                  </p>
                  <p className="text-xs text-muted-soft mt-1">{p.interventions_total} interventi totali</p>
                  <p className="font-serif text-4xl text-navy mt-4">{eur(p.price)}</p>
                  <p className="text-xs text-muted-soft mb-5">{eur(p.price_per_intervention)} a intervento</p>
                  {bonus?.status === "disponibile" && p.interventions_total >= 5 && (
                    <p data-testid={`plan-bonus-${m}m-${p.frequency}x`}
                      className="text-xs font-bold text-[#2f5d3e] bg-[#e8f0eb] border border-[#d1e2d5] rounded-full px-3 py-1.5 mb-4 self-start">
                      Bonus applicabile: −{eur(bonus.amount)}
                    </p>
                  )}
                  <Link to={ctaFor(p.id)} data-testid={`plan-cta-${m}m-${p.frequency}x`} className="btn-secondary mt-auto !py-2.5 text-sm">
                    Scegli
                  </Link>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <p className="text-xs text-muted-soft text-center pb-6">
        Pagamento con carta, PayPal o bonifico. In futuro sarà disponibile il rinnovo automatico con carta o PayPal.
      </p>
    </div>
  );
}
